/**
 * Service for managing datasets.
 */
import supabase from './supabaseClient';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

export interface Dataset {
  id: string;
  user_id: string;
  name: string;
  display_name?: string;
  description?: string;
  dataset_type?: string;
  total_samples?: number;
  s3_base_path: string;
  project_id?: string | null;
  split_info?: any;
  schema_info?: any;
  created_at?: string;
  updated_at?: string;
}

class DatasetService {
  /**
   * Get authentication headers.
   */
  private async getAuthHeaders(): Promise<HeadersInit> {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      throw new Error('Not authenticated');
    }

    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${session.access_token}`,
    };
  }

  /**
   * List all datasets for the current user.
   */
  async listDatasets(projectId?: string): Promise<Dataset[]> {
    const headers = await this.getAuthHeaders();
    const url = projectId 
      ? `${BACKEND_URL}/api/storage/datasets?project_id=${projectId}`
      : `${BACKEND_URL}/api/storage/datasets`;

    const response = await fetch(url, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to list datasets');
    }

    const data = await response.json();
    console.log('[DatasetService] Response:', data);
    return data.datasets || [];
  }

  /**
   * Get a specific dataset by ID.
   */
  async getDataset(datasetId: string): Promise<Dataset> {
    const headers = await this.getAuthHeaders();

    const response = await fetch(`${BACKEND_URL}/api/storage/datasets/${datasetId}`, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to get dataset');
    }

    return response.json();
  }

  /**
   * Delete a dataset.
   */
  async deleteDataset(datasetId: string): Promise<void> {
    const headers = await this.getAuthHeaders();

    const response = await fetch(`${BACKEND_URL}/api/storage/datasets/${datasetId}`, {
      method: 'DELETE',
      headers,
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to delete dataset');
    }
  }
}

export const datasetService = new DatasetService();
export default datasetService;

